import React, { useState, useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Eye } from "lucide-react";
import styles from "./NotificationsImproved.module.scss";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import { setViewNotification } from "../../store/actions";

interface NotificationsImprovedProps {
  newNotifications: number;
  solicitudesList: any[];
  isMobile?: boolean;
  onClose?: () => void;
}

const NotificationsImproved: React.FC<NotificationsImprovedProps> = ({
  newNotifications,
  solicitudesList,
  isMobile = false,
  onClose,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'projects' | 'materials' | 'social' | 'work'>('all');
  const [visibleCount, setVisibleCount] = useState(5);
  const [localNotifications, setLocalNotifications] = useState<any[]>([]);
  const [localUnreadCount, setLocalUnreadCount] = useState(0);
  const [markingInProgress, setMarkingInProgress] = useState<Set<string>>(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterTabsRef = useRef<HTMLDivElement>(null);
  const { ocultarEnMobile } = useNavVisibility();


  useEffect(() => {
    if (solicitudesList && markingInProgress.size === 0 && !isSyncing) {
      setLocalNotifications(solicitudesList);
      setLocalUnreadCount(solicitudesList.filter((item: any) => item.view !== true).length);
    }
  }, [solicitudesList, markingInProgress.size, isSyncing]);

  const categorizeNotifications = (notifications: any[]) => {
    return notifications.reduce((acc, notification) => {
      const type = notification.type;
      
      if (type.includes('POSTULACION') || type.includes('PRESUPUESTO') || type.includes('COMMENT_ENVIADO') || type.includes('COMMENT_RESPONDIDO')) {
        acc.projects.push(notification);
      } else if (type.includes('QUOTATION')) {
        acc.materials.push(notification);
      } else if (type.includes('POST_') || type.includes('PROFILE_VIEW')) {
        acc.social.push(notification);
      } else if (type.includes('JOB_') || type.includes('CONTACT') || type.includes('COINCIDENCE') || type.includes('QUESTION')) {
        acc.work.push(notification);
      } else {
        acc.other.push(notification);
      }
      
      return acc;
    }, {
      projects: [],
      materials: [],
      social: [],
      work: [],
      other: []
    });
  };

  const getFilteredNotifications = () => {
    if (!localNotifications.length) return [];
    
    const categorized = categorizeNotifications(localNotifications);
    
    switch (filter) {
      case 'unread':
        return localNotifications.filter(n => n.view !== true);
      case 'projects':
        return categorized.projects;
      case 'materials':
        return categorized.materials;
      case 'social':
        return categorized.social;
      case 'work':
        return categorized.work;
      default:
        return localNotifications;
    }
  };

  const filteredNotifications = getFilteredNotifications();
  const visibleNotifications = filteredNotifications.slice(0, visibleCount);
  const hasMore = filteredNotifications.length > visibleCount;

  const handleNotificationClick = () => {
    if (isMobile || ocultarEnMobile) {
      navigate('/notificaciones');
      onClose?.();
    } else {
      setShowDropdown(!showDropdown);
    }
  };

  const handleViewAll = () => {
    navigate('/notificaciones');
    setShowDropdown(false);
    onClose?.();
  };

  const handleMarkAllRead = () => {
    const unreadNotifications = localNotifications.filter(n => n.view !== true);
    
    setLocalNotifications(prev => 
      prev.map(n => ({ ...n, view: true }))
    );
    setLocalUnreadCount(0);
    
    const allIds = unreadNotifications.map(n => n.id);
    setMarkingInProgress(new Set(allIds));
    
    unreadNotifications.forEach(notification => {
      dispatch(setViewNotification(notification.id) as any);
    });
    
    setTimeout(() => {
      setMarkingInProgress(new Set());
      
      setTimeout(() => {
        setIsSyncing(true);
        if (solicitudesList) {
          setLocalNotifications(solicitudesList);
          setLocalUnreadCount(solicitudesList.filter((item: any) => item.view !== true).length);
        }
        setTimeout(() => setIsSyncing(false), 100);
      }, 1000);
    }, 500);
  };

  const handleMarkAsRead = (notificationId: string) => {
    if (markingInProgress.has(notificationId)) return;
    
    setLocalNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, view: true } : n)
    );
    setLocalUnreadCount(prev => Math.max(0, prev - 1));
    setMarkingInProgress(prev => new Set(Array.from(prev).concat(notificationId)));
    
    dispatch(setViewNotification(notificationId) as any);
    
    setTimeout(() => {
      setMarkingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
      
      setTimeout(() => {
        setIsSyncing(true);
        if (solicitudesList) {
          setLocalNotifications(solicitudesList);
          setLocalUnreadCount(solicitudesList.filter((item: any) => item.view !== true).length);
        }
        setTimeout(() => setIsSyncing(false), 100);
      }, 1000);
    }, 500);
  };

  const getNotificationText = (notification: any) => {
    const type = notification.type;
    const userSender = notification.userSender;
    const userName = userSender?.type === "COMPANY" 
      ? userSender?.razon_social 
      : `${userSender?.name || ''} ${userSender?.last_name || ''}`.trim();

    switch (type) {
      case "POSTULACION_SEND":
      case "USER_POSTULATION_SEND":
        return `${userName} se postuló a tu proyecto`;
      
      case "POSTULACION_ACCEPTED":
      case "USER_POSTULATION_ACCEPTED":
        return `Tu postulación fue aceptada`;
      
      case "POSTULACION_NEGATED":
      case "USER_POSTULATION_NEGATED":
        return `Tu postulación fue rechazada`;
      
      case "NEW_PROFILE_VIEW":
      case "USER_NEW_PROFILE_VIEW":
        return `${userName} vio tu perfil`;
      
      case "COMMENT_ENVIADO":
      case "USER_COMMENT_ENVIADO":
        return `${userName} comentó en tu proyecto`;
      
      case "COMMENT_RESPONDIDO":
      case "USER_COMMENT_RESPONDIDO":
        return `${userName} respondió tu comentario`;
      
      case "QUOTATION_SEND":
        return `${userName} envió una cotización`;
      
      case "PRESUPUESTO_ENVIADO":
        return `${userName} envió un presupuesto`;
      
      case "NEW_CONTACT_REQUEST":
        return `${userName} quiere contactarte`;
      
      case "NEW_COINCIDENCES":
        return `Nueva coincidencia con ${userName}`;
      
      case "POST_LIKE_SEND":
        return `${userName} le dio like a tu publicación`;
      
      case "POST_COMMENT_SEND":
        return `${userName} comentó tu publicación`;
      
      case "NEW_JOB_APLICATION":
        return `${userName} se postuló a tu oferta de trabajo`;
      
      case "QUESTION_RESPONDIDO":
        return `${userName} respondió tu pregunta`;
      
      default:
        return `Nueva notificación de ${userName}`;
    }
  };

  const getFormattedDate = (notification: any) => {
    try {
      const dateValue = notification.sendDate || 
                       notification.createdAt || 
                       notification.date || 
                       notification.timestamp;
      
      if (!dateValue) {
        return 'Fecha no disponible';
      }

      const date = new Date(dateValue);
      
      if (isNaN(date.getTime())) {
        return 'Fecha no disponible';
      }

      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        return 'Hoy';
      } else if (diffDays === 2) {
        return 'Ayer';
      } else if (diffDays <= 7) {
        return `Hace ${diffDays - 1} días`;
      } else {
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        });
      }
    } catch (error) {
      return 'Fecha no disponible';
    }
  };

  const getFilterCount = (filterType: string) => {
    if (!localNotifications.length) return 0;

    const categorized = categorizeNotifications(localNotifications);

    switch (filterType) {
      case 'unread':
        return localNotifications.filter(n => n.view !== true).length;
      case 'projects':
        return categorized.projects.length;
      case 'materials':
        return categorized.materials.length;
      case 'social':
        return categorized.social.length;
      case 'work':
        return categorized.work.length;
      case 'portfolio':
        return categorized.portfolio.length;
      default:
        return localNotifications.length;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (filterTabsRef.current && showDropdown) {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        filterTabsRef.current.scrollLeft += delta;
      }
    };

    if (showDropdown) {
      const filterTabs = filterTabsRef.current;
      if (filterTabs) {
        filterTabs.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
          filterTabs.removeEventListener('wheel', handleWheel);
        };
      }
    }
  }, [showDropdown]);

  useEffect(() => {
    if (filterTabsRef.current && showDropdown) {
      const activeTab = filterTabsRef.current.querySelector(`[data-tab-id="${filter}"]`) as HTMLElement;
      if (activeTab) {
        const container = filterTabsRef.current;
        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        
        const tabCenter = tabRect.left + tabRect.width / 2;
        const containerCenter = containerRect.left + containerRect.width / 2;
        const scrollOffset = tabCenter - containerCenter;
        
        container.scrollTo({
          left: container.scrollLeft + scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  }, [filter, showDropdown]);

  const renderDropdown = () => {
    if (isMobile || ocultarEnMobile || !showDropdown) return null;

    return (
      <div ref={dropdownRef} className={styles.notificationsDropdown}>
        <div className={styles.notificationsHeader}>
              <div className={styles.headerTitle}>
                <Bell size={20} />
                <h3>Notificaciones</h3>
                {localUnreadCount > 0 && (
                  <span className={styles.unreadCount}>{localUnreadCount}</span>
                )}
              </div>
          <div className={styles.headerActions}>
            <button 
              className={styles.actionButton}
              onClick={handleMarkAllRead}
              title="Marcar todas como leídas"
            >
              <Check size={16} />
            </button>
            <button 
              className={styles.actionButton}
              onClick={handleViewAll}
              title="Ver todas"
            >
              <Eye size={16} />
            </button>
          </div>
        </div>

        <div className={styles.filterTabsWrapper}>
          <div ref={filterTabsRef} className={styles.filterTabs}>
            {[
              { id: 'all', label: 'Todas' },
              { id: 'unread', label: 'No leídas' },
              { id: 'projects', label: 'Proyectos' },
              { id: 'materials', label: 'Materiales' },
              { id: 'social', label: 'Social' },
              { id: 'work', label: 'Trabajo' }
            ].map(tab => {
              const count = getFilterCount(tab.id);
              return (
                <button
                  key={tab.id}
                  data-tab-id={tab.id}
                  className={`${styles.filterTab} ${filter === tab.id ? styles.active : ''}`}
                  onClick={() => setFilter(tab.id as any)}
                >
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={styles.filterCount}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.notificationsContent}>
          {visibleNotifications.length > 0 ? (
            <>
                  <div className={styles.notificationsList}>
                    {visibleNotifications.map((notification, index) => (
                      <div 
                        key={index} 
                        className={`${styles.notificationItem} ${notification.view !== true ? styles.unread : ''} ${markingInProgress.has(notification.id) ? styles.marking : ''}`}
                        onClick={() => notification.view !== true && handleMarkAsRead(notification.id)}
                        style={{ cursor: notification.view !== true ? 'pointer' : 'default' }}
                      >
                        <div className={styles.notificationContent}>
                          <p className={styles.notificationText}>
                            {getNotificationText(notification)}
                          </p>
                          <small className={styles.notificationDate}>
                            {getFormattedDate(notification)}
                          </small>
                          {notification.view !== true && (
                            <div className={styles.markAsReadHint}>
                              Click para marcar como leída
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
              
              {hasMore && (
                <div className={styles.loadMore}>
                  <button 
                    className={styles.loadMoreButton}
                    onClick={() => setVisibleCount(prev => prev + 5)}
                  >
                    Ver más ({filteredNotifications.length - visibleCount} restantes)
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <Bell size={48} />
              <p>No hay notificaciones</p>
              <small>Selecciona un filtro diferente o verifica más tarde</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.notificationsContainer}>
      <button
        className={`${styles.notificationButton} ${showDropdown ? styles.active : ''}`}
        onClick={handleNotificationClick}
        title="Notificaciones"
      >
            <div className={styles.notificationIcon}>
              <Bell size={20} />
              {localUnreadCount > 0 && (
                <span className={styles.notificationBadge}>
                  {localUnreadCount > 99 ? '99+' : localUnreadCount}
                </span>
              )}
            </div>
      </button>
      
      {renderDropdown()}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  newNotifications: state.dashboard.newNotifications,
  solicitudesList: state.dashboard.solicitudesList,
});

export default connect(mapStateToProps)(NotificationsImproved);
